import React, { useState, useEffect, useCallback, useRef, useContext } from "react";
import { getData, postData, putData } from "../../../../services/Fetchnodeservices"; // Adjust imports as needed
// ⚠️ IMPORTANT: Adjust this path to exactly match where your ThemeContext is located!
import { ThemeContext } from "../../../../context/ThemeContext"; 

// ─────────────────────────────────────────────────────────────────────────────
// ICONS
// ─────────────────────────────────────────────────────────────────────────────
const PlusIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const CloseIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const UploadIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="17 8 12 3 7 8" />
    <line x1="12" y1="3" x2="12" y2="15" />
  </svg>
);

const TrashIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
  </svg>
);

const PriorityHighIcon = ({ color }) => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
  </svg>
);

// ─────────────────────────────────────────────────────────────────────────────
// ADD FEEDBACK DIALOG COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
const AddFeedbackDialog = ({ onClose, onSave, editingFeedback, theme, isDark }) => {
  const [formData, setFormData] = useState({
    title: editingFeedback?.title || "",
    platform: editingFeedback?.platform || "other",
    category: editingFeedback?.category || "bug",
    priority: editingFeedback?.priority || "medium",
    points: editingFeedback?.points ? (typeof editingFeedback.points === 'string' ? JSON.parse(editingFeedback.points) : editingFeedback.points) : [{ description: "", images: [] }],
    dev_notes: editingFeedback?.dev_notes || "",
  });
  const [uploadedImages, setUploadedImages] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handlePointChange = (index, value) => {
    const newPoints = [...formData.points];
    newPoints[index] = { ...newPoints[index], description: value };
    setFormData(prev => ({ ...prev, points: newPoints }));
  };

  const addPoint = () => {
    setFormData(prev => ({
      ...prev,
      points: [...prev.points, { description: "", images: [] }]
    }));
  };

  const removePoint = (index) => {
    const newPoints = formData.points.filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, points: newPoints }));
  };

  const handleImageUpload = async (files) => {
    const newImages = [];
    for (let file of files) {
      const reader = new FileReader();
      const imageUrl = await new Promise((resolve) => {
        reader.onloadend = () => resolve(reader.result);
        reader.readAsDataURL(file);
      });
      newImages.push(imageUrl);
    }
    setUploadedImages(prev => [...prev, ...newImages]);
  };

  const handleFileInput = (e) => {
    const files = Array.from(e.target.files);
    handleImageUpload(files);
  };

  const handleDragOver = (e) => { e.preventDefault(); setDragActive(true); };
  const handleDragLeave = (e) => { e.preventDefault(); setDragActive(false); };
  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    const files = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith('image/'));
    handleImageUpload(files);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.title.trim()) return;
    
    onSave({
      title: formData.title,
      platform: formData.platform,
      category: formData.category,
      priority: formData.priority,
      points: JSON.stringify(formData.points.filter(p => p.description.trim())),
      dev_notes: formData.dev_notes,
      status: editingFeedback?.status || "pending",
    });
    onClose();
  };

  const dialogStyles = {
    overlay: {
      position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: "rgba(0, 0, 0, 0.7)", display: "flex", alignItems: "center",
      justifyContent: "center", zIndex: 1000, backdropFilter: "blur(4px)",
    },
    dialog: {
      width: "90%", maxWidth: "580px", background: theme.surface,
      borderRadius: "20px", boxShadow: "0 25px 40px -12px rgba(0,0,0,0.4)",
      overflow: "hidden", animation: "fadeIn 0.2s ease-out",
    },
    header: {
      display: "flex", justifyContent: "space-between", alignItems: "center",
      padding: "18px 24px", borderBottom: `1px solid ${theme.border}`,
    },
    title: { fontSize: "20px", fontWeight: 700, color: theme.text, margin: 0 },
    closeBtn: {
      background: "none", border: "none", cursor: "pointer", color: theme.textMuted,
      padding: "4px", display: "flex", borderRadius: "8px", transition: "all 0.2s",
    },
    body: { padding: "24px", maxHeight: "65vh", overflowY: "auto" },
    formGroup: { marginBottom: "20px" },
    label: { display: "block", marginBottom: "8px", fontSize: "13px", fontWeight: 600, color: theme.textMuted },
    required: { color: theme.danger, marginLeft: "4px" },
    input: {
      width: "100%", padding: "12px 14px", background: theme.surfaceAlt,
      border: `1px solid ${theme.border}`, borderRadius: "12px", fontSize: "14px",
      color: theme.text, outline: "none", boxSizing: "border-box", transition: "all 0.2s",
    },
    textarea: {
      width: "100%", padding: "12px 14px", background: theme.surfaceAlt,
      border: `1px solid ${theme.border}`, borderRadius: "12px", fontSize: "14px",
      color: theme.text, outline: "none", fontFamily: "inherit", resize: "vertical",
      minHeight: "80px", boxSizing: "border-box", transition: "all 0.2s",
    },
    select: {
      width: "100%", padding: "12px 14px", background: theme.surfaceAlt,
      border: `1px solid ${theme.border}`, borderRadius: "12px", fontSize: "14px",
      color: theme.text, outline: "none", cursor: "pointer", transition: "all 0.2s",
    },
    rowTwoColumns: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" },
    pointsItem: { display: "flex", alignItems: "center", gap: "8px", marginBottom: "10px" },
    pointsNumber: {
      width: "28px", height: "28px", borderRadius: "6px", background: theme.surfaceAlt,
      display: "flex", alignItems: "center", justifyContent: "center", fontSize: "12px",
      fontWeight: 600, color: theme.textMuted, flexShrink: 0,
    },
    pointsInput: {
      flex: 1, padding: "8px 12px", background: theme.surfaceAlt,
      border: `1px solid ${theme.border}`, borderRadius: "8px", fontSize: "13px",
      color: theme.text, outline: "none", transition: "all 0.2s",
    },
    removePointBtn: { background: "none", border: "none", cursor: "pointer", padding: "4px", color: theme.danger, display: "flex" },
    addPointBtn: {
      display: "flex", alignItems: "center", gap: "6px", padding: "6px 12px",
      background: "transparent", border: `1px solid ${theme.primary}`, borderRadius: "8px",
      fontSize: "12px", fontWeight: 500, color: theme.primary, cursor: "pointer", marginTop: "8px",
    },
    footer: {
      display: "flex", justifyContent: "flex-end", gap: "12px", padding: "16px 24px",
      borderTop: `1px solid ${theme.border}`, background: theme.surfaceAlt,
    },
    cancelBtn: {
      padding: "10px 20px", background: "transparent", border: `1px solid ${theme.border}`,
      borderRadius: "10px", fontSize: "14px", fontWeight: 500, color: theme.textMuted, cursor: "pointer",
    },
    saveBtn: {
      padding: "10px 24px", background: theme.gradient, border: "none", borderRadius: "10px",
      fontSize: "14px", fontWeight: 600, color: "#fff", cursor: "pointer",
      boxShadow: `0 4px 6px -1px ${theme.primary}30`, transition: "all 0.2s",
    },
  };

  return (
    <div style={dialogStyles.overlay} onClick={onClose}>
      <div style={dialogStyles.dialog} onClick={(e) => e.stopPropagation()}>
        <div style={dialogStyles.header}>
          <h3 style={dialogStyles.title}>{editingFeedback ? "Edit Feedback" : "New Feedback"}</h3>
          <button style={dialogStyles.closeBtn} onClick={onClose}
            onMouseEnter={(e) => e.currentTarget.style.background = theme.surfaceAlt}
            onMouseLeave={(e) => e.currentTarget.style.background = "none"}>
            <CloseIcon />
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <div style={dialogStyles.body}>
            <div style={dialogStyles.formGroup}>
              <label style={dialogStyles.label}>Title <span style={dialogStyles.required}>*</span></label>
              <input type="text" name="title" style={dialogStyles.input} placeholder="e.g. Add filter option in admin panel" value={formData.title} onChange={handleChange} required autoFocus />
            </div>
            <div style={dialogStyles.rowTwoColumns}>
              <div style={dialogStyles.formGroup}>
                <label style={dialogStyles.label}>Platform *</label>
                <select name="platform" style={dialogStyles.select} value={formData.platform} onChange={handleChange} required>
                  <option value="user_app">User App</option>
                  <option value="vendor_app">Vendor App</option>
                  <option value="rider_app">Rider App</option>
                  <option value="website">Website</option>
                  <option value="admin_panel">Admin Panel</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div style={dialogStyles.formGroup}>
                <label style={dialogStyles.label}>Category *</label>
                <select name="category" style={dialogStyles.select} value={formData.category} onChange={handleChange} required>
                  <option value="bug">Bug</option>
                  <option value="feature">Feature</option>
                  <option value="ui_fix">UI Fix</option>
                  <option value="change">Change</option>
                </select>
              </div>
            </div>
            <div style={dialogStyles.rowTwoColumns}>
              <div style={dialogStyles.formGroup}>
                <label style={dialogStyles.label}>Priority *</label>
                <select name="priority" style={dialogStyles.select} value={formData.priority} onChange={handleChange} required>
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>
              <div style={dialogStyles.formGroup}>
                <label style={dialogStyles.label}>Dev Notes</label>
                <textarea name="dev_notes" style={dialogStyles.textarea} placeholder="Notes for developer..." value={formData.dev_notes} onChange={handleChange} rows="2" />
              </div>
            </div>
            <div style={dialogStyles.formGroup}>
              <label style={dialogStyles.label}>Points / Description</label>
              {formData.points.map((point, index) => (
                <div key={index} style={dialogStyles.pointsItem}>
                  <div style={dialogStyles.pointsNumber}>{index + 1}</div>
                  <input type="text" style={dialogStyles.pointsInput} placeholder="Describe the issue or feature..." value={point.description} onChange={(e) => handlePointChange(index, e.target.value)} />
                  {formData.points.length > 1 && (
                    <button type="button" style={dialogStyles.removePointBtn} onClick={() => removePoint(index)}>
                      <TrashIcon />
                    </button>
                  )}
                </div>
              ))}
              <button type="button" style={dialogStyles.addPointBtn} onClick={addPoint}
                onMouseEnter={(e) => { e.currentTarget.style.background = theme.primaryLight; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}>
                <PlusIcon /> Add Point
              </button>
            </div>
          </div>
          <div style={dialogStyles.footer}>
            <button type="button" style={dialogStyles.cancelBtn} onClick={onClose}
              onMouseEnter={(e) => e.currentTarget.style.background = theme.border}
              onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}>Cancel</button>
            <button type="submit" style={dialogStyles.saveBtn}
              onMouseEnter={(e) => e.currentTarget.style.filter = "brightness(1.1)"}
              onMouseLeave={(e) => e.currentTarget.style.filter = "brightness(1)"}>
              {editingFeedback ? "Update" : "Submit"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// SUB-COMPONENTS
// ─────────────────────────────────────────────────────────────────────────────
const StatusBadge = ({ status, theme, isDark }) => {
  const config = {
    pending: { bg: isDark ? "rgba(245,158,11,0.2)" : "rgba(245,158,11,0.1)", color: theme.warning },
    in_progress: { bg: isDark ? "rgba(59,130,246,0.2)" : "rgba(59,130,246,0.1)", color: theme.info },
    completed: { bg: isDark ? "rgba(34,197,94,0.2)" : "rgba(34,197,94,0.1)", color: theme.success },
  };
  const c = config[status] || config.pending;
  return <span style={{ display: "inline-block", padding: "3px 10px", borderRadius: "20px", fontSize: "10px", fontWeight: 600, background: c.bg, color: c.color }}>{status?.replace('_', ' ')}</span>;
};

const TypeBadge = ({ category, theme, isDark }) => {
  const config = {
    feature: { bg: isDark ? "rgba(139,92,246,0.2)" : "rgba(139,92,246,0.1)", color: "#8b5cf6" },
    bug: { bg: isDark ? "rgba(239,68,68,0.2)" : "rgba(239,68,68,0.1)", color: theme.danger },
    change: { bg: isDark ? "rgba(59,130,246,0.2)" : "rgba(59,130,246,0.1)", color: theme.info },
    ui_fix: { bg: isDark ? "rgba(16,185,129,0.2)" : "rgba(16,185,129,0.1)", color: theme.success },
  };
  const c = config[category] || config.feature;
  return <span style={{ display: "inline-block", padding: "2px 8px", borderRadius: "12px", fontSize: "10px", fontWeight: 500, background: c.bg, color: c.color }}>{category}</span>;
};

const PriorityBadge = ({ priority, theme, isDark }) => {
  const config = {
    urgent: { bg: isDark ? "rgba(239,68,68,0.2)" : "rgba(239,68,68,0.1)", color: theme.danger },
    high: { bg: isDark ? "rgba(239,68,68,0.15)" : "rgba(239,68,68,0.08)", color: "#f97316" },
    medium: { bg: isDark ? "rgba(245,158,11,0.2)" : "rgba(245,158,11,0.1)", color: theme.warning },
    low: { bg: isDark ? "rgba(148,163,184,0.2)" : "rgba(148,163,184,0.1)", color: theme.textMuted },
  };
  const c = config[priority] || config.medium;
  return <span style={{ fontSize: "10px", fontWeight: 600, color: c.color, display: "flex", alignItems: "center", gap: "3px" }}>
    {priority === "urgent" && <PriorityHighIcon color={c.color} />} {priority}
  </span>;
};

const StatCard = ({ title, count, theme }) => (
  <div style={{
    background: theme.surface, border: `1px solid ${theme.border}`, borderRadius: "12px",
    padding: "12px 20px", textAlign: "center", flex: 1, minWidth: "80px",
    boxShadow: "0 2px 4px rgba(0,0,0,0.05)", transition: "all 0.3s ease",
  }}>
    <div style={{ fontSize: "24px", fontWeight: 700, color: theme.text }}>{count}</div>
    <div style={{ fontSize: "11px", fontWeight: 500, color: theme.textMuted, marginTop: "4px" }}>{title}</div>
  </div>
);

const TabButton = ({ label, isActive, onClick, theme }) => (
  <button onClick={onClick} style={{
    padding: "6px 14px", borderRadius: "20px", fontSize: "12px", fontWeight: 500,
    background: isActive ? theme.primary : "transparent",
    color: isActive ? "#fff" : theme.textMuted,
    border: isActive ? `1px solid ${theme.primary}` : `1px solid ${theme.border}`,
    cursor: "pointer", transition: "all 0.2s",
  }}>{label}</button>
);

const FeedbackCard = ({ feedback, theme, isDark, onStatusChange }) => {
  const [expanded, setExpanded] = useState(false);
  
  let pointsData = [];
  try {
    pointsData = typeof feedback.points === 'string' ? JSON.parse(feedback.points) : (feedback.points || []);
  } catch (e) {
    pointsData = [{ description: feedback.points || "" }];
  }

  return (
    <div style={{
      background: theme.surface, border: `1px solid ${theme.border}`, borderRadius: "12px",
      padding: "16px", marginBottom: "12px", transition: "all 0.2s",
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "8px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
          <span style={{ fontSize: "13px", fontWeight: 700, color: theme.primary }}>#{feedback.id}</span>
          <span style={{ fontSize: "14px", fontWeight: 600, color: theme.text }}>{feedback.title}</span>
        </div>
        <div style={{ display: "flex", gap: "8px" }}>
          <select value={feedback.status} onChange={(e) => onStatusChange(feedback.id, e.target.value)} style={{
            padding: "4px 8px", borderRadius: "6px", fontSize: "11px",
            background: theme.surfaceAlt, border: `1px solid ${theme.border}`,
            color: theme.text, cursor: "pointer",
          }}>
            <option value="pending">Pending</option>
            <option value="in_progress">In Progress</option>
            <option value="completed">Completed</option>
          </select>
          <button onClick={() => setExpanded(!expanded)} style={{ background: "none", border: "none", cursor: "pointer", color: theme.textMuted, padding: "4px" }}>
            {expanded ? "▲" : "▼"}
          </button>
        </div>
      </div>

      <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", marginBottom: "8px" }}>
        <TypeBadge category={feedback.category} theme={theme} isDark={isDark} />
        <PriorityBadge priority={feedback.priority} theme={theme} isDark={isDark} />
        <span style={{ fontSize: "11px", color: theme.textMuted }}>{feedback.platform?.replace('_', ' ')}</span>
      </div>

      <div style={{ fontSize: "11px", color: theme.textMuted, display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
        <span>by {feedback.created_by_name}</span>
        <span>•</span>
        <span>{new Date(feedback.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
        {feedback.status !== "completed" && (
          <>
            <span>•</span>
            <StatusBadge status={feedback.status} theme={theme} isDark={isDark} />
          </>
        )}
        {feedback.completed_by_name && (
          <>
            <span>•</span>
            <span>Completed by {feedback.completed_by_name}</span>
          </>
        )}
      </div>

      {expanded && (
        <div style={{ marginTop: "12px", paddingTop: "12px", borderTop: `1px solid ${theme.border}` }}>
          <div style={{ fontSize: "12px", color: theme.textMuted }}>
            <strong>Points:</strong>
            {pointsData.map((point, idx) => (
              <div key={idx} style={{ marginTop: "6px", marginLeft: "8px" }}>• {point.description}</div>
            ))}
            {feedback.dev_notes && (
              <>
                <div style={{ marginTop: "12px" }}><strong>Dev Notes:</strong></div>
                <div style={{ marginTop: "6px" }}>{feedback.dev_notes}</div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
const DevFeedback = ({ isDark = true }) => {
  // 🛡️ SAFE CONTEXT CONSUMPTION WITH FALLBACK
  const themeContext = useContext(ThemeContext);
  const isDarkMode = themeContext?.currentTheme === 'dark';
  
  const fallbackTheme = {
    background: isDarkMode ? "#0c1018" : "#f8fafc",
    surface: isDarkMode ? "#141824" : "#ffffff",
    border: isDarkMode ? "rgba(255,255,255,0.07)" : "#e2e8f0",
    textPrimary: isDarkMode ? "#f1f5f9" : "#0f172a",
    textSecondary: isDarkMode ? "#94a3b8" : "#64748b",
    textMuted: isDarkMode ? "#64748b" : "#94a3b8",
    primary: "#6366f1",
    primaryLight: "rgba(99, 102, 241, 0.15)",
    primaryGradient: "linear-gradient(135deg, #6366f1, #8b5cf6)",
  };

  const brandTheme = themeContext?.theme || fallbackTheme;
  
  // 🔴 UNIFIED THEME OBJECT
  const theme = {
    bg: brandTheme.background,
    surface: brandTheme.surface,
    surfaceAlt: isDarkMode ? "#1e293b" : "#f1f5f9",
    border: brandTheme.border,
    text: brandTheme.textPrimary,
    textMuted: brandTheme.textMuted,
    primary: brandTheme.primary,
    primaryLight: brandTheme.primaryLight || `${brandTheme.primary}25`,
    gradient: brandTheme.primaryGradient || `linear-gradient(135deg, ${brandTheme.primary}, ${brandTheme.primary}dd)`,
    success: "#22c55e",
    warning: "#f59e0b",
    danger: "#ef4444",
    info: brandTheme.primary,
  };

  const [feedbacks, setFeedbacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTab, setSelectedTab] = useState("All");
  const [sortBy, setSortBy] = useState("priority");
  const [sortOrder, setSortOrder] = useState("desc");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingFeedback, setEditingFeedback] = useState(null);

  const platforms = ["All", "user_app", "vendor_app", "rider_app", "website", "admin_panel", "other"];

  const fetchFeedbacks = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getData("devfeedback/DevFeedback");
      const visibleFeedbacks = (response.data || []).filter(f => f.hide === 0);
      setFeedbacks(visibleFeedbacks);
    } catch (error) {
      console.error("Error fetching feedbacks:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFeedbacks();
  }, [fetchFeedbacks]);

  const stats = {
    pending: feedbacks.filter(f => f.status === "pending").length,
    inProgress: feedbacks.filter(f => f.status === "in_progress").length,
    completed: feedbacks.filter(f => f.status === "completed").length,
    total: feedbacks.length,
  };

  let filteredFeedbacks = selectedTab === "All"
    ? feedbacks
    : feedbacks.filter(f => f.platform === selectedTab);

  filteredFeedbacks = [...filteredFeedbacks].sort((a, b) => {
    if (sortBy === "priority") {
      const priorityOrder = { urgent: 0, high: 1, medium: 2, low: 3 };
      return sortOrder === "asc"
        ? priorityOrder[a.priority] - priorityOrder[b.priority]
        : priorityOrder[b.priority] - priorityOrder[a.priority];
    } else if (sortBy === "id") {
      return sortOrder === "asc" ? a.id - b.id : b.id - a.id;
    } else if (sortBy === "date") {
      return sortOrder === "asc"
        ? new Date(a.created_at) - new Date(b.created_at)
        : new Date(b.created_at) - new Date(a.created_at);
    }
    return 0;
  });

  const handleStatusChange = async (id, newStatus) => {
    try {
      await putData(`devfeedback/DevFeedback/${id}`, { status: newStatus });
      setFeedbacks(prev => prev.map(f => f.id === id ? { ...f, status: newStatus } : f));
    } catch (error) {
      console.error("Error updating status:", error);
    }
  };

  const handleAddFeedback = () => {
    setEditingFeedback(null);
    setIsDialogOpen(true);
  };

  const handleSaveFeedback = async (feedbackData) => {
    try {
      if (editingFeedback) {
        await putData(`devfeedback/DevFeedback/${editingFeedback.id}`, feedbackData);
      } else {
        await postData("devfeedback/DevFeedback", feedbackData);
      }
      await fetchFeedbacks();
      setIsDialogOpen(false);
      setEditingFeedback(null);
    } catch (error) {
      console.error("Error saving feedback:", error);
    }
  };

  const toggleSortOrder = () => {
    setSortOrder(prev => prev === "asc" ? "desc" : "asc");
  };

  const styles = {
    container: {
      background: "transparent",
      fontFamily: "'Segoe UI', 'DM Sans', sans-serif",
      padding: "20px 24px",
      transition: "background 0.3s ease",
    },
    header: {
      display: "flex", justifyContent: "space-between", alignItems: "center",
      marginBottom: "20px", flexWrap: "wrap", gap: "16px",
    },
    title: { fontSize: "20px", fontWeight: 700, color: theme.text, margin: 0 },
    addBtn: {
      display: "flex", alignItems: "center", gap: "6px", padding: "8px 18px",
      background: theme.gradient, color: "#fff", border: "none", borderRadius: "10px",
      fontSize: "13px", fontWeight: 600, cursor: "pointer", transition: "all 0.2s",
      boxShadow: `0 4px 6px -1px ${theme.primary}30`,
    },
    statsRow: { display: "flex", gap: "12px", marginBottom: "24px", flexWrap: "wrap" },
    tabsRow: {
      display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between",
      marginBottom: "20px", gap: "16px",
    },
    tabs: { display: "flex", gap: "8px", flexWrap: "wrap" },
    sortSection: { display: "flex", alignItems: "center", gap: "12px" },
    sortLabel: { fontSize: "12px", fontWeight: 500, color: theme.textMuted },
    sortButtons: { display: "flex", gap: "6px" },
    sortBtn: (isActive) => ({
      padding: "5px 12px", borderRadius: "20px", fontSize: "12px", fontWeight: 500,
      background: isActive ? theme.primary : "transparent",
      color: isActive ? "#fff" : theme.textMuted,
      border: isActive ? `1px solid ${theme.primary}` : `1px solid ${theme.border}`,
      cursor: "pointer", transition: "all 0.2s",
    }),
    orderBtn: {
      background: "none", border: "none", cursor: "pointer", color: theme.textMuted,
      fontSize: "12px", padding: "5px 8px", borderRadius: "6px", transition: "all 0.2s",
    },
    feedbackList: { marginTop: "20px" },
    emptyState: { textAlign: "center", padding: "60px", color: theme.textMuted, fontSize: "14px" },
    loadingState: { textAlign: "center", padding: "60px", color: theme.textMuted },
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingState}>Loading feedbacks...</div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: scale(0.96); }
          to { opacity: 1; transform: scale(1); }
        }
        .sort-btn:hover { background: ${theme.primaryLight} !important; }
        .order-btn:hover { background: ${theme.surfaceAlt} !important; }
      `}</style>

      <div style={styles.header}>
        <h2 style={styles.title}>Dev Feedback</h2>
        <button style={styles.addBtn} onClick={handleAddFeedback}
          onMouseEnter={(e) => e.currentTarget.style.filter = "brightness(1.1)"}
          onMouseLeave={(e) => e.currentTarget.style.filter = "brightness(1)"}>
          <PlusIcon /> New Feedback
        </button>
      </div>

      <div style={styles.statsRow}>
        <StatCard title="Pending" count={stats.pending} theme={theme} />
        <StatCard title="In Progress" count={stats.inProgress} theme={theme} />
        <StatCard title="Completed" count={stats.completed} theme={theme} />
        <StatCard title="Total" count={stats.total} theme={theme} />
      </div>

      <div style={styles.tabsRow}>
        <div style={styles.tabs}>
          {platforms.map(platform => (
            <TabButton
              key={platform}
              label={platform === "All" ? "All" : platform.replace('_', ' ')}
              isActive={selectedTab === platform}
              onClick={() => setSelectedTab(platform)}
              theme={theme}
            />
          ))}
        </div>

        <div style={styles.sortSection}>
          <span style={styles.sortLabel}>Sort by:</span>
          <div style={styles.sortButtons}>
            <button className="sort-btn" style={styles.sortBtn(sortBy === "priority")} onClick={() => setSortBy("priority")}>Priority</button>
            <button className="sort-btn" style={styles.sortBtn(sortBy === "id")} onClick={() => setSortBy("id")}>ID</button>
            <button className="sort-btn" style={styles.sortBtn(sortBy === "date")} onClick={() => setSortBy("date")}>Date</button>
          </div>
          <button className="order-btn" style={styles.orderBtn} onClick={toggleSortOrder}>
            {sortOrder === "asc" ? "↑" : "↓"}
          </button>
        </div>
      </div>

      <div style={styles.feedbackList}>
        {filteredFeedbacks.length === 0 ? (
          <div style={styles.emptyState}>No feedback found</div>
        ) : (
          filteredFeedbacks.map(feedback => (
            <FeedbackCard
              key={feedback.id}
              feedback={feedback}
              theme={theme}
              isDark={isDarkMode}
              onStatusChange={handleStatusChange}
            />
          ))
        )}
      </div>

      {isDialogOpen && (
        <AddFeedbackDialog
          onClose={() => { setIsDialogOpen(false); setEditingFeedback(null); }}
          onSave={handleSaveFeedback}
          editingFeedback={editingFeedback}
          theme={theme}
          isDark={isDarkMode}
        />
      )}
    </div>
  );
};

export default DevFeedback;